import { useState, useEffect, useCallback } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Gauge, DownloadSimple, ArrowClockwise, CarSimple, Path, CurrencyGbp, Check, FilePdf } from "@phosphor-icons/react";

import DateInput from "../components/DateInput";
function fmtDrive(mins) {
  if (mins == null) return "—";
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
}

function VarianceChip({ v }) {
  if (v == null) return <span className="text-slate-400">—</span>;
  const over = v > 0;
  return (
    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
      over ? "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
           : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"}`}>
      {over ? "+" : ""}{v} mi
    </span>
  );
}

export default function MileageLogPage() {
  const today = new Date().toISOString().slice(0, 10);
  const twoWeeksAgo = new Date(Date.now() - 13 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(twoWeeksAgo);
  const [end, setEnd] = useState(today);
  const [userId, setUserId] = useState("");
  const [team, setTeam] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [rate, setRate] = useState("");
  const [savedRate, setSavedRate] = useState(0);
  const [savingRate, setSavingRate] = useState(false);

  useEffect(() => {
    API.get("/in-house/mileage-rate").then(({ data }) => {
      setSavedRate(data.pence_per_mile || 0);
      setRate(data.pence_per_mile ? String(data.pence_per_mile) : "");
    }).catch(() => {});
  }, []);

  const saveRate = async () => {
    const val = parseFloat(rate) || 0;
    setSavingRate(true);
    try {
      const { data } = await API.put("/in-house/mileage-rate", { pence_per_mile: val });
      setSavedRate(data.pence_per_mile);
      toast.success(data.pence_per_mile > 0 ? `Mileage rate saved — ${data.pence_per_mile}p per mile` : "Mileage rate cleared");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save rate");
    } finally { setSavingRate(false); }
  };

  useEffect(() => {
    API.get("/in-house/team").then(({ data }) => setTeam(data.team || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (userId) params.user_id = userId;
      const { data } = await API.get("/in-house/mileage-log", { params });
      setRows(data.rows || []);
      setTotals(data.totals || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load mileage log");
    } finally { setLoading(false); }
  }, [start, end, userId]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { start, end };
      if (userId) params.user_id = userId;
      const res = await API.get("/in-house/mileage-log/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mileage-log_${start}_${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const params = { start, end };
      if (userId) params.user_id = userId;
      const res = await API.get("/in-house/mileage-log/export.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const opName = (team.find(u => u._id === userId)?.name || "all-operatives").toLowerCase().replace(/\s+/g, "-");
      a.download = `mileage-report_${opName}_${start}_${end}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Mileage report downloaded");
    } catch (e) {
      toast.error("PDF generation failed");
    } finally { setExportingPdf(false); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="mileage-log-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Gauge className="h-7 w-7 text-blue-600" weight="duotone" /> Mileage Log
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Planned route mileage vs estimated actual (from geotagged site arrivals) per operative, per day.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={exporting || rows.length === 0}
                  data-testid="mileage-export-btn">
            <DownloadSimple className="h-4 w-4 mr-2" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button onClick={exportPdf} disabled={exportingPdf || rows.length === 0}
                  className="bg-blue-700 hover:bg-blue-800 text-white"
                  title={userId ? "PDF report for the selected operative and date range" : "PDF report for all operatives in the date range"}
                  data-testid="mileage-pdf-btn">
            <FilePdf className="h-4 w-4 mr-2" /> {exportingPdf ? "Generating…" : "Generate PDF"}
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">From</Label>
            <DateInput value={start} onChange={(e) => setStart(e.target.value)} data-testid="mileage-start-input" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <DateInput value={end} onChange={(e) => setEnd(e.target.value)} data-testid="mileage-end-input" />
          </div>
          <div>
            <Label className="text-xs">Operative</Label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="mileage-operative-select">
              <option value="">All operatives</option>
              {team.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Rate (pence per mile)</Label>
            <div className="flex gap-1.5">
              <Input type="number" min={0} max={500} step="0.5" placeholder="e.g. 45"
                     value={rate} onChange={(e) => setRate(e.target.value)}
                     data-testid="mileage-rate-input" />
              <Button variant="outline" size="icon" className="shrink-0" onClick={saveRate}
                      disabled={savingRate} title="Save rate" data-testid="mileage-rate-save-btn">
                <Check className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="mileage-refresh-btn">
            <ArrowClockwise className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
        {savedRate <= 0 && (
          <p className="text-[11px] text-amber-600 mt-2" data-testid="mileage-rate-hint">
            Set a pence-per-mile rate (HMRC approved rate is 45p) to price each day for expense claims.
          </p>
        )}
      </div>

      {totals && (
        <div className={`grid ${savedRate > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"} gap-2`}>
          <div className="bg-card border border-border rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-slate-900 dark:text-slate-100 inline-flex items-center gap-1.5">
              <Path className="h-4 w-4 text-blue-500" />{totals.planned_miles} mi
            </p>
            <p className="text-[11px] text-slate-500 uppercase font-semibold">Planned</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-slate-900 dark:text-slate-100 inline-flex items-center gap-1.5">
              <CarSimple className="h-4 w-4 text-emerald-500" />{totals.actual_miles} mi
            </p>
            <p className="text-[11px] text-slate-500 uppercase font-semibold">Actual (est)</p>
          </div>
          {savedRate > 0 && (
            <div className="bg-card border border-border rounded-lg p-3 text-center" data-testid="mileage-claim-total">
              <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1">
                <CurrencyGbp className="h-4 w-4" />{(totals.claim_cost ?? 0).toFixed(2)}
              </p>
              <p className="text-[11px] text-slate-500 uppercase font-semibold">Claim @ {savedRate}p/mi</p>
            </div>
          )}
          <div className="bg-card border border-border rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{totals.days}</p>
            <p className="text-[11px] text-slate-500 uppercase font-semibold">Day rows</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-400 py-8 text-center">Loading mileage log…</p>
      ) : rows.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center" data-testid="mileage-empty-state">
          <Gauge className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No mileage data in this range yet.</p>
          <p className="text-xs text-slate-400 mt-1">Rows appear when an operative plans a day route or checks in on site with location enabled.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-left font-semibold">Operative</th>
                <th className="px-4 py-3 text-right font-semibold">Stops</th>
                <th className="px-4 py-3 text-right font-semibold">Planned mi</th>
                <th className="px-4 py-3 text-right font-semibold hidden sm:table-cell">Drive time</th>
                <th className="px-4 py-3 text-right font-semibold">Actual mi (est)</th>
                <th className="px-4 py-3 text-right font-semibold hidden md:table-cell">Arrivals</th>
                <th className="px-4 py-3 text-right font-semibold">Variance</th>
                {savedRate > 0 && <th className="px-4 py-3 text-right font-semibold">Claim £</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.user_id}-${r.date}`} className="border-t border-border" data-testid={`mileage-row-${i}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{r.user_name}</td>
                  <td className="px-4 py-3 text-right">{r.planned_stops ?? "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold">{r.planned_miles ?? "—"}</td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell">{fmtDrive(r.planned_drive_minutes)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{r.actual_miles ?? "—"}</td>
                  <td className="px-4 py-3 text-right hidden md:table-cell">{r.actual_stops || "—"}</td>
                  <td className="px-4 py-3 text-right"><VarianceChip v={r.variance_miles} /></td>
                  {savedRate > 0 && (
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400" data-testid={`mileage-claim-${i}`}>
                      {r.claim_cost != null ? `£${r.claim_cost.toFixed(2)}` : "—"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-400">
        Actual mileage is estimated from geotagged "Arrive on site" check-ins (home → arrivals in order → home, road-adjusted). Days without geotagged arrivals show "—". Claim value is priced on actual miles where available, otherwise planned miles.
      </p>
    </div>
  );
}
